/**
 * BrandsSection — Cartera de marcas con logos reales
 * Fondo blanco para que los logos PNG se vean perfectos.
 * Reutilizable en home y páginas de servicios.
 */

interface Brand {
  name: string;
  logo: string;
  category: string;
}

const BRANDS: Brand[] = [
  {
    name: 'Growatt',
    logo: 'https://public.readdy.ai/ai/img_res/c2303988-6592-4dca-a410-0c5e7a7401b2.png',
    category: 'Inversores',
  },
  {
    name: 'MUST Energy',
    logo: 'https://storage.readdy-site.link/project_files/4f188a96-057d-4221-af00-461f9392b074/5ee24866-d627-4a8c-9175-3db24e20147c_WhatsApp-Image-2026-04-30-at-9.55.08-AM-3.jpeg?v=3c3b464f7aca0a51e573f3bd2c068c0e',
    category: 'Inversores',
  },
  {
    name: 'SAKO',
    logo: 'https://storage.readdy-site.link/project_files/4f188a96-057d-4221-af00-461f9392b074/91e90759-68a8-47cd-b2f8-c413aba0103b_WhatsApp-Image-2026-04-30-at-9.55.08-AM-2.jpeg?v=033df336a78621a43adf8849993f6e47',
    category: 'Inversores',
  },
  {
    name: 'Jinko Solar',
    logo: 'https://readdy.ai/api/search-image?query=JinkoSolar%20solar%20panels%20brand%20logo%20clean%20white%20background%20official%20logotype%20dark%20blue%20text%20minimal%20professional&width=240&height=100&seq=brand-jinko-v3&orientation=landscape',
    category: 'Paneles',
  },
  {
    name: 'LONGi',
    logo: 'https://readdy.ai/api/search-image?query=LONGi%20Solar%20panels%20brand%20logo%20clean%20white%20background%20official%20logotype%20red%20text%20minimal%20professional&width=240&height=100&seq=brand-longi-v3&orientation=landscape',
    category: 'Paneles',
  },
  {
    name: 'Canadian Solar',
    logo: 'https://readdy.ai/api/search-image?query=Canadian%20Solar%20panels%20brand%20logo%20clean%20white%20background%20official%20logotype%20dark%20text%20minimal%20professional&width=240&height=100&seq=brand-canadian-v3&orientation=landscape',
    category: 'Paneles',
  },
  {
    name: 'Infini Solar',
    logo: 'https://storage.readdy-site.link/project_files/4f188a96-057d-4221-af00-461f9392b074/bcadeb9d-b190-46f8-9ffc-91b3dbc836e3_WhatsApp-Image-2026-04-30-at-9.55.09-AM-4.jpeg?v=0990d30f54e74027d8a8bea814739b01',
    category: 'Inversores',
  },
  {
    name: 'Milexus',
    logo: 'https://public.readdy.ai/ai/img_res/edited_f20ccdedaab2a50ee5a590097973b6e1_0e76a8f6.jpg',
    category: 'Paneles',
  },
];

interface BrandsSectionProps {
  /** Tema oscuro (para páginas de servicios con fondo negro) o claro (home) */
  theme?: 'dark' | 'light';
  title?: string;
  subtitle?: string;
}

export default function BrandsSection({
  theme = 'light',
  title = 'Cartera de marcas',
  subtitle = 'Trabajamos con los fabricantes más reconocidos del mercado solar',
}: BrandsSectionProps) {
  const isDark = theme === 'dark';

  return (
    <section
      className="py-10 md:py-14"
      style={{ background: isDark ? 'rgba(245,158,11,0.06)' : '#ffffff' }}
    >
      <div className="max-w-5xl mx-auto px-4 md:px-6">

        {/* Header */}
        <div className="mb-8 md:mb-10">
          {isDark ? (
            <>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 flex items-center justify-center rounded-xl" style={{ background: 'rgba(75,191,223,0.15)' }}>
                  <i className="ri-award-line text-sm" style={{ color: '#4bbfdf' }} />
                </div>
                <h2 className="font-bold text-base text-neutral-100">{title}</h2>
              </div>
              <p className="text-[12px] text-neutral-500 ml-10">{subtitle}</p>
            </>
          ) : (
            <>
              <span className="text-xs font-bold uppercase tracking-widest block mb-2" style={{ color: '#4bbfdf' }}>Nuestros partners</span>
              <h2
                className="font-extrabold text-gray-900 leading-tight"
                style={{ fontSize: 'clamp(1.4rem, 2.5vw, 2rem)', letterSpacing: '-0.02em' }}
              >
                {title}
              </h2>
              <p className="text-gray-500 text-sm mt-1.5 max-w-lg">{subtitle}</p>
            </>
          )}
        </div>

        {/* Brands grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
          {BRANDS.map((brand) => (
            <div
              key={brand.name}
              className="group flex flex-col items-center justify-center gap-2 rounded-2xl p-4 md:p-5 transition-all duration-200"
              style={
                isDark
                  ? {
                      background: '#ffffff',
                      border: '1px solid rgba(255,255,255,0.08)',
                    }
                  : {
                      background: '#ffffff',
                      border: '1.5px solid #f3f4f6',
                    }
              }
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor = isDark ? 'rgba(75,191,223,0.40)' : '#e5e7eb';
                (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor = isDark ? 'rgba(255,255,255,0.08)' : '#f3f4f6';
                (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
              }}
            >
              {/* Logo */}
              <div className="w-full flex items-center justify-center" style={{ height: '72px' }}>
                <img
                  src={brand.logo}
                  alt={`Logo ${brand.name}`}
                  className="max-w-full max-h-full object-contain"
                  style={{ maxHeight: '64px', maxWidth: '160px' }}
                  loading="lazy"
                  decoding="async"
                  onError={(e) => {
                    // Fallback: show brand name as text
                    const img = e.target as HTMLImageElement;
                    img.style.display = 'none';
                    const parent = img.parentElement;
                    if (parent && !parent.querySelector('.brand-fallback')) {
                      const span = document.createElement('span');
                      span.className = 'brand-fallback font-black text-gray-800 text-base tracking-tight';
                      span.textContent = brand.name;
                      parent.appendChild(span);
                    }
                  }}
                />
              </div>

              {/* Name + category */}
              <div className="text-center">
                <p className="font-bold text-gray-800 text-[12px] leading-tight">{brand.name}</p>
                <p className="text-[10px] text-gray-400 mt-0.5 font-medium">{brand.category}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom note */}
        <p className={`text-center text-[11px] mt-6 font-medium ${isDark ? 'text-neutral-600' : 'text-gray-400'}`}>
          <i className="ri-checkbox-circle-line mr-1" />
          Todos los productos son originales con garantía de fábrica
        </p>
      </div>
    </section>
  );
}
